"use client";

import { useQuery } from "@tanstack/react-query";
import { parseAsString, useQueryState } from "nuqs";
import { useMapBbox } from "@/features/map/hooks/useMapBbox";
import { formatCamera, parseCamera } from "@/features/map/utils/cameraHash";
import { useProjectConfigs } from "@/features/project/ProjectContext";

/**
 * Address search, against Mapbox's geocoding API.
 *
 * A place the tenant hasn't mapped is still somewhere the user wants to go —
 * typing a street name should move the map there even though no POI matches it.
 *
 * The legacy app reached this through `@mapbox/mapbox-gl-geocoder`, whose value
 * is its UI: an input, a dropdown and a marker. We already have all three, and
 * results have to appear in the *same* dropdown as the POI suggestions rather
 * than a second one beside it — so this calls the API the plugin wraps and skips
 * the plugin.
 */

/** The place types that count as an address rather than a point of interest. */
const ADDRESS_TYPES =
  "country,region,postcode,district,place,locality,neighborhood,address";

/** How many of each kind to ask for; the caller shows fewer. */
const LIMIT = 5;

/** Zoom to settle on when the user picks a place. */
const PLACE_ZOOM = 16;

export interface Place {
  id: string;
  name: string;
  center: [number, number];
}

interface MapboxFeature {
  id?: string;
  place_name?: string;
  center?: [number, number];
}

async function geocode(params: {
  text: string;
  token: string;
  proximity: string | undefined;
  countries: string | undefined;
  types?: string;
  signal: AbortSignal | undefined;
}): Promise<Place[]> {
  const search = new URLSearchParams({
    access_token: params.token,
    limit: String(LIMIT),
  });
  // Results near what the user is looking at, not near the prime meridian.
  if (params.proximity) search.set("proximity", params.proximity);
  if (params.countries) search.set("country", params.countries);
  if (params.types) search.set("types", params.types);

  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      params.text
    )}.json?${search}`,
    { signal: params.signal }
  );
  if (!response.ok) return [];

  const body = (await response.json()) as { features?: MapboxFeature[] };
  return (body.features ?? [])
    .filter((feature): feature is MapboxFeature & { center: [number, number] } =>
      Array.isArray(feature.center)
    )
    .map((feature) => ({
      id: feature.id ?? feature.place_name ?? String(feature.center),
      name: feature.place_name ?? "",
      center: feature.center,
    }));
}

export function useGeocoder(text: string) {
  const configs = useProjectConfigs();
  const bbox = useMapBbox((state) => state.bbox);

  const token = configs.mapboxAccessToken ?? process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
  const query = text.trim();
  const enabled = !!configs.geocoderEnabled && !!token && !!query;

  // The viewport's centre, so "Rue de la Paix" finds the one in this city. Taken
  // from the tracked bbox rather than the map instance because this runs outside
  // the map's provider.
  const proximity = bbox
    ? `${(bbox[0] + bbox[2]) / 2},${(bbox[1] + bbox[3]) / 2}`
    : undefined;

  const result = useQuery({
    queryKey: ["geocoder", { query, proximity, countries: configs.geocoderCountries }],
    queryFn: async ({ signal }) => {
      const common = {
        text: query,
        token: token!,
        proximity,
        countries: configs.geocoderCountries,
        signal,
      };

      // Some tenants want addresses and points of interest listed apart, which
      // takes two calls — Mapbox returns one ranked list otherwise.
      if (configs.geocoderSeparatePOIs) {
        const [addresses, pois] = await Promise.all([
          geocode({ ...common, types: ADDRESS_TYPES }),
          geocode({ ...common, types: "poi" }),
        ]);
        return { addresses, pois };
      }

      return { addresses: await geocode(common), pois: [] as Place[] };
    },
    enabled,
    placeholderData: (previous) => previous,
  });

  return {
    addresses: result.data?.addresses ?? [],
    pois: result.data?.pois ?? [],
  };
}

/**
 * Move the map to a place.
 *
 * Written to the `map` URL parameter rather than called on the map instance: the
 * camera already lives there (see `useCameraUrlSync`), the search bar renders
 * outside the map's provider, and going through the URL means the view the user
 * landed on is the view they can share.
 */
export function useFlyToPlace() {
  const [value, setValue] = useQueryState("map", parseAsString);

  return (place: Place) => {
    const current = parseCamera(value);
    void setValue(
      formatCamera({
        zoom: Math.max(current?.zoom ?? 0, PLACE_ZOOM),
        center: place.center,
        // A geocoded jump keeps the tilt the user had set rather than resetting
        // a 3D view to flat.
        bearing: current?.bearing ?? 0,
        pitch: current?.pitch ?? 0,
      })
    );
  };
}
